import { useEffect, useMemo, useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Modal from "../../common/Modal";
import { votersAPI } from "../../../services/api";

function StatusBadge({ status }) {
  const label = status?.label || "";
  return (
    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
      {label}
    </span>
  );
}

export default function AddVotersToPrecinctModal({ isOpen, onClose, onAssign }) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});

  const normalizeVoter = (v) => {
    if (!v || typeof v !== "object") return {};
    const resident = v.resident || {};
    const id = v.id ?? v.voter_id ?? v.resident_id ?? resident.resident_id ?? v.id_number ?? v.voters_id_no;
    const full_name = v.full_name || v.name || resident.full_name || resident.name || "";
    const contact_number = v.contact_number || v.phone || v.mobile || resident.contact_number || "";
    const id_number = v.voter_id || v.id_number || v.voters_id_no || undefined;
    const status = typeof v.status === "string" ? { label: v.status } : v.status;
    return { ...v, id, full_name, contact_number, id_number, status };
  };

  const totalPages = useMemo(() => {
    if (!total || !perPage) return 1;
    return Math.max(1, Math.ceil(total / perPage));
  }, [total, perPage]);

  const anySelected = useMemo(() => Object.keys(selected).length > 0, [selected]);

  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    setSelected({});
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;
    async function fetchVoters() {
      setLoading(true);
      try {
        const res = await votersAPI.getAll({ page, per_page: perPage, q: query });
        const data = res?.data?.data || res?.data || {};
        const list = data.voters || data.items || data.data || [];
        const meta = data.meta || res?.data?.meta || {};
        if (!cancelled) {
          setItems(Array.isArray(list) ? list.map(normalizeVoter) : []);
          setTotal(meta.total || data.total || list.length);
        }
      } catch (e) {
        if (!cancelled) {
          setItems([]);
          setTotal(0);
        }
        console.error("Failed to load voters", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchVoters();
    return () => {
      cancelled = true;
    };
  }, [page, perPage, query]);

  const toggleSelect = (item) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = item;
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = items.map((i) => i.id).filter(Boolean);
    const allSelected = pageIds.every((id) => selected[id]);
    if (allSelected) {
      const next = { ...selected };
      pageIds.forEach((id) => delete next[id]);
      setSelected(next);
    } else {
      const next = { ...selected };
      items.forEach((i) => {
        if (i.id) next[i.id] = i;
      });
      setSelected(next);
    }
  };

  const footer = (
    <div className="flex items-center justify-between">
      <div className="text-sm text-gray-600">
        Selected: <span className="font-medium">{Object.keys(selected).length}</span>
      </div>
      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-md bg-gray-100 text-gray-700"
        >
          Cancel
        </button>
        <button
          disabled={!anySelected}
          onClick={() => {
            const arr = Object.values(selected);
            onAssign?.(arr);
            onClose?.();
          }}
          className={`px-4 py-2 rounded-md text-white ${anySelected ? "bg-indigo-600 hover:bg-indigo-700" : "bg-indigo-300 cursor-not-allowed"}`}
        >
          Assign Voter
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Voters" size="5xl" footer={footer}>
      <div className="mb-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, ID number, or contact..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            className="w-full rounded-lg border border-gray-300 pl-10 pr-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="border rounded-xl overflow-hidden">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 text-gray-600 text-sm">
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  onChange={toggleSelectAll}
                  checked={items.length > 0 && items.every((i) => selected[i.id])}
                />
              </th>
              <th className="px-4 py-3 text-left">Voter</th>
              <th className="px-4 py-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-10 text-center text-gray-500 text-sm">
                  No voters found
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={3} className="px-4 py-10 text-center text-gray-500 text-sm">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && items.map((v, idx) => {
              const initials = (v.full_name || v.name || "?")
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((s) => s[0]?.toUpperCase())
                .join("");
              return (
                <tr key={v.id ?? v.voter_id ?? v.id_number ?? idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={!!selected[v.id]}
                      onChange={() => toggleSelect(v)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold">
                        {initials || "?"}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {v.full_name || v.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {(v.voter_id || v.id_number) ? (
                            <span className="inline-flex items-center rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 mr-2">
                              {v.voter_id || v.id_number}
                            </span>
                          ) : null}
                          {v.contact_number || v.phone || v.mobile ? (
                            <span>{v.contact_number || v.phone || v.mobile}</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={v.status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
        <div>
          Showing {items.length > 0 ? (perPage * (page - 1) + 1) : 0} to {Math.min(perPage * page, total)} of {total} results
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-2 py-1 rounded border border-gray-300 disabled:opacity-50"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            {"<"}
          </button>
          <span className="px-3 py-1 rounded border border-indigo-300 text-indigo-700 bg-indigo-50">
            {page}
          </span>
          <button
            className="px-2 py-1 rounded border border-gray-300 disabled:opacity-50"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            {">"}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span>Show:</span>
          <select
            className="border rounded px-2 py-1"
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            {[3, 5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}
